<template>
  <div class="m-page">
    <van-search
      v-model="keyword"
      shape="round"
      placeholder="代码或名称筛选"
      clearable
    />

    <div class="toolbar">
      <van-dropdown-menu>
        <van-dropdown-item v-model="selectedPeriod" :options="periodOptions" @change="loadTableData" />
        <van-dropdown-item v-model="sortBy" :options="sortOptions" />
      </van-dropdown-menu>
      <span class="tip">{{ filtered.length }} 条</span>
    </div>

    <van-loading v-if="tableLoading" class="page-loading" vertical>加载中...</van-loading>
    <van-empty v-else-if="!filtered.length" description="无匹配数据" />

    <div v-else>
      <div v-for="(row, idx) in paged" :key="row.code" class="card">
        <div class="card-head">
          <span class="rank">{{ idx + 1 + (page - 1) * pageSize }}</span>
          <a class="code" :href="row.codeLink" target="_blank" rel="noopener noreferrer">{{ row.code }}</a>
          <a class="name" :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">{{ row.name }}</a>
        </div>
        <div class="card-row">
          <span :class="{ accent: sortBy === 'shares' }">{{ row.shares }} 家</span>
          <span :class="{ accent: sortBy === 'marketValue' }">{{ row.marketValue }} 亿</span>
          <span>{{ row.action }}</span>
          <span>{{ row.changePercent }}</span>
        </div>
      </div>
      <div v-if="filtered.length > pageSize" class="pager">
        <van-pagination
          v-model="page"
          :total-items="filtered.length"
          :items-per-page="pageSize"
          force-ellipses
        />
      </div>
    </div>
  </div>
</template>

<script>
import { fetchFundPeriods, fetchFundHoldings } from '../utils/fundHoldingsApi.js'
import { eastmoneyCodeLink } from '../utils/stockLink.js'

// 期数据内存缓存（同一会话内切换报告期不重复请求）
const fundTableCache = new Map()

export default {
  name: 'MobileFund',
  data() {
    return {
      selectedPeriod: '',
      periodOptions: [],
      sortBy: 'shares',
      sortOptions: [
        { text: '按持股家数', value: 'shares' },
        { text: '按持股市值', value: 'marketValue' },
      ],
      tableData: [],
      keyword: '',
      tableLoading: false,
      loadToken: 0,
      page: 1,
      pageSize: 50,
    }
  },
  computed: {
    filtered() {
      const kw = String(this.keyword || '').trim()
      const key = this.sortBy === 'marketValue' ? 'marketValue' : 'shares'
      const list = kw
        ? this.tableData.filter((row) => {
          return String(row.code || '').includes(kw) || String(row.name || '').includes(kw)
        })
        : this.tableData.slice()
      return list.sort((a, b) => {
        const av = Number(a[key]) || 0
        const bv = Number(b[key]) || 0
        return bv - av
      })
    },
    paged() {
      const start = (this.page - 1) * this.pageSize
      return this.filtered.slice(start, start + this.pageSize)
    },
  },
  watch: {
    keyword() { this.page = 1 },
    sortBy() { this.page = 1 },
  },
  created() {
    this.initPeriods()
  },
  methods: {
    eastmoneyCodeLink,
    async initPeriods() {
      try {
        const periods = await fetchFundPeriods()
        if (!periods.length) return
        this.periodOptions = periods.map((p) => ({ text: p.period, value: p.period }))
        this.selectedPeriod = this.periodOptions[0].value
        this.loadTableData()
      } catch (err) {
        this.$toast?.fail(`报告期列表加载失败: ${err.message}`)
      }
    },
    async loadTableData() {
      if (!this.selectedPeriod) return
      const token = ++this.loadToken
      const cached = fundTableCache.get(this.selectedPeriod)
      if (cached) {
        this.tableData = cached
        this.page = 1
        return
      }
      this.tableLoading = true
      try {
        const rows = await fetchFundHoldings(this.selectedPeriod)
        if (token !== this.loadToken) return
        fundTableCache.set(this.selectedPeriod, rows)
        this.tableData = rows
        this.page = 1
      } catch (err) {
        if (token === this.loadToken) this.$toast?.fail(`持仓数据加载失败: ${err.message}`)
      } finally {
        if (token === this.loadToken) this.tableLoading = false
      }
    },
  },
}
</script>

<style scoped>
.m-page { padding-bottom: 12px; }
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toolbar >>> .van-dropdown-menu {
  flex: 1;
  height: 40px;
}
.tip {
  padding-right: 12px;
  font-size: 12px;
  color: #969799;
  white-space: nowrap;
}
.page-loading { padding: 48px 0; }
.card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
}
.card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rank { font-size: 12px; color: #969799; min-width: 24px; }
.code, .name { color: #1989fa; text-decoration: none; font-size: 14px; }
.name {
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 6px;
  font-size: 12px;
  color: #646566;
}
.accent {
  color: #1989fa;
  font-weight: 600;
}
.pager { padding: 12px 8px; }
</style>
